import React, { useState } from 'react'
import {
    makeStyles,
    Box,
    Container,
    Typography,
    TextField,
    IconButton,
    Link,
} from '@material-ui/core'
import "./styles/global.css"
import { clsx } from 'clsx';
import Logo from "./images/SugarCribs.svg";
import Twitter from "./images/social/twitter.svg";
import Instagram from "./images/social/instagram.svg";
import { ContainedButton, OutlinedButton, ContainedPinkButton } from './components/buttons/Buttons'
import heroImage from "./images/heroImage.png";
import feature_1 from "./images/features/feature_1.svg";
import feature_2 from "./images/features/feature_2.svg";
import feature_3 from "./images/features/feature_3.svg";
import image_1 from "./images/image_1.png";
import playButton from "./images/playButton.png";
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
    // Global
    root: {
        fontFamily: "Roboto, Arial, sans-serif",
        backgroundColor: '#0a0a0a',
        color: '#ffffff',
        minHeight: '100vh',
        overflowX: 'hidden',
    },
    sectionTitle: {
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: "64px",
        fontWeight: 400,
        lineHeight: 1,
        textAlign: "left",
        [theme.breakpoints.down('sm')]: {
            fontSize: '2rem',
        },
    },
    sectionText: {
        fontSize: '18px',
        opacity: 0.7,
        lineHeight: 1.4,
        fontWeight: 200,
    },


    // Header
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2, 4),
        backgroundColor: "#111111",
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
    },
    logo: {
        fontFamily: '"Playfair Display", serif',
        fontSize: '1.5rem',
        fontWeight: 500,
        color: '#ffffff',
        textDecoration: 'none',
    },
    nav: {
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    navLink: {
        color: '#ffffff',
        textDecoration: 'none',
        fontSize: '16px',
        fontWeight: 400,
        opacity: 0.9,
        transition: 'opacity 0.2s',
        '&:hover': {
            opacity: 0.7,
            textDecoration: 'none',
            color: '#ffffff',
        },
    },

    // Hero Section
    heroSection: {
        minHeight: "513px",
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(12, 2),
        backgroundImage: `url(${heroImage})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        position: 'relative',
        marginTop: 88,
        "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundColor: "#111111CC",
        },
    },
    heroSectionContainer: {
        zIndex: 1,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    heroTitle: {
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: '82px',
        fontWeight: 400,
        marginBottom: "24px",
        lineHeight: 1,
        [theme.breakpoints.down('sm')]: {
            fontSize: '2.5rem',
        },
    },
    heroTitleItalic: {
        fontStyle: 'italic',
    },
    heroSubtitle: {
        fontSize: '18px',
        fontWeight: 200,
        maxWidth: 597,
        marginBottom: "40px",
        lineHeight: 1.4,
        opacity: 0.7,
        textAlign: "center"
    },
    heroButtons: {
        display: 'flex',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    primaryButton: {
        backgroundColor: '#ffffff',
        color: '#0a0a0a',
        textTransform: 'none',
        fontSize: '0.875rem',
        fontWeight: 500,
        padding: theme.spacing(1.5, 4),
        borderRadius: 4,
        '&:hover': {
            backgroundColor: '#f0f0f0',
        },
    },
    secondaryButton: {
        backgroundColor: 'transparent',
        color: '#ffffff',
        textTransform: 'none',
        fontSize: '0.875rem',
        fontWeight: 500,
        padding: theme.spacing(1.5, 4),
        borderRadius: 4,
        border: '1px solid rgba(255,255,255,0.3)',
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
        },
    },

    // Features Section
    featuresSection: {
        maxWidth: "1081px",
        margin: "0 auto",
        padding: "60px 21px",
        textAlign: "center"
    },
    featuresHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 44,
        flexWrap: 'wrap',
        gap: 20,
    },
    featuresTitle: {
        maxWidth: "50%",
    },
    featuresDescription: {
        width: "50%",
        maxWidth: "531px",
        textAlign: "left"
    },
    featuresGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: "16px",
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
        },
    },
    featureCard: {
        textAlign: 'center',
        padding: "24px",
        backgroundColor: "#1B1B1B"
    },
    featureIcon: {
        width: 60,
        height: 60,
        marginBottom: theme.spacing(3),
        opacity: 0.8,
    },
    featureTitle: {
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: '28px',
        fontWeight: 500,
        marginBottom: 8,
    },
    featureDescription: {
        fontSize: '16px',
        lineHeight: 1.3,
        fontWeight: 300,
        color: "#FFFFFFE5",
    },

    // Lifestyle Section
    lifestyleTitle: {
        marginBottom: 16,
        textAlign: "center"
    },
    lifestyleSubtitle: {
        marginBottom: theme.spacing(6),
        maxWidth: 500,
        margin: '0 auto',
    },
    videoContainer: {
        position: 'relative',
        margin: '0 auto',
        marginBottom: "48px",
        aspectRatio: '16/10',
        maxHeight: 607,
        "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundColor: '#00000080',
        },
    },
    videoImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    playButton: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 50,
        height: 50,
        zIndex: 1,
        transition: "all 0.3s ease",
        '&:hover': {
            scale: 1.1
        },
    },
    requestAccessButton: {
        fontSize: '20px',
        fontWeight: 400,
    },

    // Curated Section
    curatedSection: {
        padding: "80px 21px"
    },
    curatedDescription: {
        margin: "auto 0"
    },
    collectionGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 20,
        marginBottom: theme.spacing(6),
    },
    collectionCard: {
        // aspectRatio: '3/4',
        backgroundColor: "#1B1B1B",
        overflow: 'hidden',
    },
    collectionImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    viewCollectionButton: {
        fontSize: "20px"
    },

    // Creator Section
    creatorSection: {
        padding: "60px 0"
    },
    creatorContent: {
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: "#1B1B1B",
        [theme.breakpoints.down('md')]: {
            flexWrap: "wrap",
        },
    },
    creatorImage: {
        width: "28%",
        height: "100%",
        maxHeight: "552px",
        objectFit: 'cover',
        order: 0,
        [theme.breakpoints.down('md')]: {
            width: "50%",
            flex: "1 1 50%",
        },
    },
    creatorText: {
        textAlign: 'center',
        maxWidth: 640,
        padding: "70px 43.5px",
        order: 0,
        [theme.breakpoints.down('md')]: {
            order: 3,
            margin: "auto"
        },
    },
    creatorTitle: {
        color: "#FFD0E1",
        marginBottom: "24px",
        textAlign: "center"
    },
    creatorDescription: {
        marginBottom: "44px",
    },

    // Waitlist Section
    waitListSection: {
        padding: "80px 0"
    },
    waitlistTitle: {
        fontFamily: '"Baskervville", serif',
        fontSize: '58px',
        fontWeight: 400,
        marginBottom: "24px",
        [theme.breakpoints.down('sm')]: {
            fontSize: '2rem',
        },
    },
    waitlistSubtitle: {
        fontSize: '20px',
    },
    waitlistForm: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: "62px",
        maxWidth: 400,
        margin: '40px auto 0',
    },
    waitlistInput: {
        width: '100%',
        "& .MuiInput-underline:before": {
            borderBottomColor: "#FFFFFF80",
        },
        "& .MuiInputBase-input": {
            color: "#FFFFFF",
            fontSize: "20px",
            fontWeight: 200,
        },
    },
    waitlistButton: {
        fontSize: '20px',
    },

    // Footer
    footer: {
        backgroundColor: '#1B1B1B',
        borderTop: '1px solid #1F1F1F',
        padding: "32px 70px",
        [theme.breakpoints.down('xs')]: {
            padding: "32px 21px",
        }
    },
    footerContent: {
        display: 'grid',
        gridTemplateColumns: 'auto 200px 200px 200px',
        gap: "20px",
        marginBottom: "24px",
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: 'repeat(3, 1fr)',
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
        },
    },
    footerBrand: {
        maxWidth: 310,
        [theme.breakpoints.down('md')]: {
            gridColumn: "1 / -1",
            marginBottom: 12
        },
    },
    footerTagline: {
        fontSize: '16px',
        fontWeight: 300,
        lineHeight: 1.1,
        marginTop: "20px"
    },
    footerColumn: {},
    footerColumnTitle: {
        fontSize: '18px',
        fontWeight: 700,
        marginBottom: "20px",
    },
    footerLink: {
        display: 'block',
        color: '#FFFFFFCC',
        textDecoration: 'none',
        fontSize: '16px',
        marginBottom: "12px",
        lineHeight: 1.1,
        fontWeight: 400,
        transition: 'opacity 0.2s',
        '&:hover': {
            opacity: 0.8,
            textDecoration: 'none',
            color: '#FFFFFFCC',
        },
    },
    footerBottom: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: "24px",
        borderTop: '1px solid #1F1F1F',
        flexWrap: 'wrap',
        gap: 24
    },
    footerSocial: {
    },
    socialLabelText: {
        fontSize: '18px',
        fontWeight: 700,
        fontFamily: '"TT Norms Pro", serif',
        marginBottom: 20
    },
    socialLabel: {
        display: "flex",
        gap: 12
    },
    socialIcon: {
        color: '#ffffff',
        width: 24,
        height: 24,
        '&:hover': {
            opacity: 0.8,
        },
    },
    footerCopyright: {
        fontSize: '16px',
    },
}))

const GeneralLandingPage: React.FC = () => {
    const classes = useStyles()
    const [email, setEmail] = useState('')
    const history = useHistory();

    return (
        <Box className={classes.root}>
            {/* Header */}
            <Box component="header" className={classes.header}>
                <Link href="#" className={classes.logo}>
                    <img src={Logo} alt="icon" width={141} height={32} />
                </Link>
                <Box component="nav" className={classes.nav}>
                    <Link href="#" className={classes.navLink}>Discover</Link>
                    <Link href="#" className={classes.navLink}>The Vault</Link>
                    <Link href="#" className={classes.navLink}>Genesis Collection</Link>
                    <Link href="#" className={classes.navLink}>Join the Waitlist</Link>
                    <OutlinedButton text="Log In" onClick={()=> history.push("/login")}/>
                    <ContainedButton text="Apply as Creator" />
                </Box>
            </Box>

            {/* Hero Section */}
            <Box className={classes.heroSection}>
                <Box className={classes.heroSectionContainer}>
                    <Typography variant="h1" className={classes.heroTitle}>
                        The inner circle <span className={classes.heroTitleItalic}>awaits.</span>
                    </Typography>
                    <Typography className={classes.heroSubtitle}>
                        Mainstream platforms are a race to the bottom. <br />
                        Step behind the velvet rope. SugarCribs is the invite-only platform for discovering premium creators in a curated, private, and luxury environment.
                    </Typography>
                    <Box className={classes.heroButtons}>
                        <ContainedButton text="Request Member Access" />
                        <OutlinedButton text="Apply as Creator" />
                    </Box>
                </Box>
            </Box>

            {/* Features Section */}
            <Box className={classes.featuresSection}>
                <Box className={classes.featuresHeader}>
                    <Typography variant="h2" className={clsx(classes.sectionTitle, classes.featuresTitle)}>
                        An elevated way <br /> to engage.
                    </Typography>
                    <Typography className={clsx(classes.sectionText, classes.featuresDescription)}>
                        Your time is valuable. Stop scrolling through endless, uncurated noise. SugarCribs is designed entirely around intentional matching, elite privacy, and premium experiences.
                    </Typography>
                </Box>
                <Box className={classes.featuresGrid}>
                    <Box className={classes.featureCard}>
                        <img className={classes.featureIcon} src={feature_1} alt="Elite Curation" />
                        <Typography className={classes.featureTitle}>Elite Curation</Typography>
                        <Typography className={classes.featureDescription}>
                            Engage exclusively with the top tier of independent creators. We hand-select our creators to ensure a standard of unparalleled quality.
                        </Typography>
                    </Box>
                    <Box className={classes.featureCard}>
                        <img className={classes.featureIcon} src={feature_2} alt="Total Discretion" />
                        <Typography className={classes.featureTitle}>Total Discretion</Typography>
                        <Typography className={classes.featureDescription}>
                            A privacy-first architecture built to protect your identity. Strict verification ensures a secure, high-intent environment for all members.
                        </Typography>
                    </Box>
                    <Box className={classes.featureCard}>
                        <img className={classes.featureIcon} src={feature_3} alt="Premium Access" />
                        <Typography className={classes.featureTitle}>Premium Access</Typography>
                        <Typography className={classes.featureDescription}>
                            Unlock exclusive VIP subscriptions, discreet 1-on-1 direct messaging, and high-value custom requests.
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Lifestyle Section */}
            <Box className={classes.featuresSection}>
                <Typography variant="h2" className={clsx(classes.sectionTitle, classes.lifestyleTitle)}>
                    The Lifestyle. Unlocked.
                </Typography>
                <Typography className={clsx(classes.sectionText, classes.lifestyleSubtitle)}>
                    Step behind the scenes with the world's most exclusive creators. Discretion guaranteed. Access granted only to the inner circle.
                </Typography>
                <Box className={classes.videoContainer}>
                    <img
                        src={image_1}
                        alt="Lifestyle preview"
                        className={classes.videoImage}
                    />
                    <IconButton className={classes.playButton}>
                        <img src={playButton} alt="play video" />
                    </IconButton>
                </Box>
                <ContainedButton text="Request Member Access" styles={classes.requestAccessButton} />
            </Box>

            {/* Curated Excellence Section */}
            <Box className={clsx(classes.featuresSection, classes.curatedSection)}>
                <Box className={classes.featuresHeader}>
                    <Typography variant="h2" className={clsx(classes.sectionTitle, classes.featuresTitle)}>
                        Curated<br />
                        <span className={classes.heroTitleItalic}>Excellence.</span>
                    </Typography>
                    <Typography className={clsx(classes.sectionText, classes.featuresDescription, classes.curatedDescription)}>
                        We hand-select every creator on the platform. From high-fashion boudoir to luxury jet-setters, discover independent visionaries who are setting the new standard for premium content.
                    </Typography>
                </Box>
                <Box className={classes.collectionGrid}>
                    <Box className={classes.collectionCard}>
                        <img
                            src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&h=400&fit=crop"
                            alt="Creator 1"
                            className={classes.collectionImage}
                        />
                    </Box>
                    <Box className={classes.collectionCard}>
                        <img
                            src="https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=300&h=400&fit=crop"
                            alt="Creator 2"
                            className={classes.collectionImage}
                        />
                    </Box>
                    <Box className={classes.collectionCard}>
                        <img
                            src="https://images.unsplash.com/photo-1517841905240-472988babdf9?w=300&h=400&fit=crop"
                            alt="Creator 3"
                            className={classes.collectionImage}
                        />
                    </Box>
                </Box>
                <OutlinedButton text="View the Genesis Collection" styles={classes.viewCollectionButton} />
            </Box>

            {/* Creator Section */}
            <Box className={classes.creatorSection}>
                <Box className={classes.creatorContent}>
                    <img
                        src="https://img.magnific.com/premium-photo/photo-studio-with-professional-equipment-bts-production-set-scenes-model-posing_647822-1239.jpg?w=2000"
                        alt="Creator left"
                        className={classes.creatorImage}
                    />
                    <Box className={classes.creatorText}>
                        <Typography variant="h3" className={clsx(classes.sectionTitle, classes.creatorTitle)}>
                            Elevate your brand. Maximize your earnings.
                        </Typography>
                        <Typography className={clsx(classes.sectionText, classes.creatorDescription)}>
                            We are currently reviewing applications for our founding Genesis Creators. Escape the noise and build your business on a platform that respects your value, protects your privacy, and connects you with high-net-worth members.
                        </Typography>
                        <ContainedPinkButton text="Explore Creator Perks" />
                    </Box>
                    <img
                        src="https://img.magnific.com/premium-photo/photo-studio-with-professional-equipment-bts-production-set-scenes-model-posing_647822-1239.jpg?w=2000"
                        alt="Creator right"
                        className={classes.creatorImage}
                    />
                </Box>
            </Box>

            {/* Waitlist Section */}
            <Box className={clsx(classes.featuresSection, classes.waitListSection)}>
                <Container maxWidth="sm">
                    <Typography variant="h2" className={classes.waitlistTitle}>
                        The list is filling up.
                    </Typography>
                    <Typography className={clsx(classes.sectionText, classes.waitlistSubtitle)}>
                        The member waitlist is strictly capped to maintain our exclusive creator-to-member ratio. Secure your invitation before the doors close permanently.
                    </Typography>
                    <Box className={classes.waitlistForm}>
                        <TextField
                            variant="standard"
                            placeholder="Enter your best email..."
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={classes.waitlistInput}
                            fullWidth
                        />
                        <ContainedButton text="Join the Waitlist" styles={classes.waitlistButton} />
                    </Box>
                </Container>
            </Box>

            {/* Footer */}
            <Box component="footer" className={classes.footer}>
                <Box className={classes.footerContent}>
                    <Box className={classes.footerBrand}>
                        <Link href="#" className={classes.logo}>
                            <img src={Logo} alt="icon" width={176} height={40} />
                        </Link>
                        <Typography className={classes.footerTagline}>
                            The exclusive platform for premium connections.
                        </Typography>
                    </Box>
                    <Box className={classes.footerColumn}>
                        <Typography variant="h4" className={classes.footerColumnTitle}>Explore:</Typography>
                        <Link href="#" className={classes.footerLink}>The Vault</Link>
                        <Link href="#" className={classes.footerLink}>Virtual Companionship</Link>
                        <Link href="#" className={classes.footerLink}>Lifestyle & Travel</Link>
                        <Link href="#" className={classes.footerLink}>Fine Art Glamour</Link>
                    </Box>
                    <Box className={classes.footerColumn}>
                        <Typography variant="h4" className={classes.footerColumnTitle}>Company:</Typography>
                        <Link href="#" className={classes.footerLink}>About Us</Link>
                        <Link href="#" className={classes.footerLink}>Creator Application</Link>
                        <Link href="#" className={classes.footerLink}>Member Waitlist</Link>
                        <Link href="#" className={classes.footerLink}>Contact</Link>
                    </Box>
                    <Box className={classes.footerColumn}>
                        <Typography variant="h4" className={classes.footerColumnTitle}>Legal:</Typography>
                        <Link href="#" className={classes.footerLink}>Terms of Service</Link>
                        <Link href="#" className={classes.footerLink}>Privacy Policy</Link>
                        <Link href="#" className={classes.footerLink}>DMCA</Link>
                        <Link href="#" className={classes.footerLink}>Compliance</Link>
                    </Box>
                </Box>
                <Box className={classes.footerBottom}>
                    <Box className={classes.footerSocial}>
                        <Typography variant="h4" className={classes.socialLabelText}>Follow Us:</Typography>
                        <Box className={classes.socialLabel}>
                            <IconButton className={classes.socialIcon} size="small">
                                <img src={Twitter} alt="icon" />
                            </IconButton>
                            <IconButton className={classes.socialIcon} size="small">
                                <img src={Instagram} alt="icon" />
                            </IconButton>
                        </Box>
                    </Box>
                    <Typography className={classes.footerCopyright}>
                        © {new Date().getFullYear()} SugarCribs. All rights reserved.
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}

export default GeneralLandingPage
